import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { createTrackingTransactionId, trackBookingConversion } from '../googleAdsTracking';

const Booking = () => {
  const [selectedServices, setSelectedServices] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleType: '',
    vehicleMake: '',
    vehicleModel: '',
    message: '',
    honeypot: '' // Bot prevention field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const serviceOptions = [
    'Maintenance Detail',
    'Full Detail',
    'Ultimate Detail',
    'Headlight Restoration',
    'Engine Bay Clean',
    'Gloss Enhancement',
    'Ceramic Coating',
    "Not sure? We'll recommend based on your needs"
  ];

  const vehicleTypes = [
    'Hatchback',
    'Saloon',
    'Small SUV',
    'Large SUV',
    'Truck',
    'Motorcycle'
  ];

  const handleServiceToggle = (service) => {
    setSelectedServices(prev => 
      prev.includes(service) 
        ? prev.filter(s => s !== service)
        : [...prev, service]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedServices.length === 0) {
      toast.error('Please select a service', {
        description: 'Choose at least one detailing service before submitting.',
      });
      return;
    }

    if (!formData.vehicleType) {
      toast.error('Please select a vehicle type', {
        description: 'Choose the vehicle type so we can prepare an accurate booking request.',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const bookingData = {
        ...formData,
        services: selectedServices,
        service: selectedServices.join(', ')
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      const data = await response.json();

      if (response.ok) {
        trackBookingConversion(data.id || createTrackingTransactionId());
        toast.success('Booking Request Sent!', {
          description: data.message || "We'll contact you shortly to confirm your appointment.",
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          vehicleType: '',
          vehicleMake: '',
          vehicleModel: '',
          message: '',
          honeypot: ''
        });
        setSelectedServices([]);
      } else {
        toast.error('Error', {
          description: data.detail || 'Failed to submit booking. Please try again.',
        });
      }
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to submit booking. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-black py-16 px-4">
      <Helmet>
        <title>Book Car Detailing Tralee | Schedule Appointment | Kingdom Auto Detailing</title>
        <meta name="description" content="Book your car detailing appointment in Tralee. Easy online booking for all services. Same-day confirmation. Professional detailing near Tralee, Co Kerry." />
        <meta name="keywords" content="book car detailing Tralee, schedule car valet, online booking car wash, car detailing appointment Tralee, book valeting Kerry" />
        <link rel="canonical" href="https://kingdomautodetailing.ie/booking" />
      </Helmet>

      <div className="max-w-6xl mx-auto">
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 text-center">
          Book Your Appointment
        </h1>
        <p className="text-xl text-gray-400 text-center mb-12">
          Schedule your detailing service and we&rsquo;ll make your vehicle look amazing
        </p>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Booking Form */}
          <div className="bg-zinc-900 rounded-3xl p-8">
            <h2 className="text-3xl font-bold text-white mb-6">Booking Details</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot field - hidden from users */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleChange}
                style={{ display: 'none' }}
                tabIndex="-1"
                autoComplete="off"
              />
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white mb-2 block">Name *</Label>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-zinc-800 border-zinc-700 text-white placeholder-gray-500 h-12"
                  />
                </div>
                <div>
                  <Label className="text-white mb-2 block">Phone *</Label>
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="Your phone number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="bg-zinc-800 border-zinc-700 text-white placeholder-gray-500 h-12"
                  />
                </div>
              </div>

              <div>
                <Label className="text-white mb-2 block">Email *</Label>
                <Input
                  type="email"
                  name="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-zinc-800 border-zinc-700 text-white placeholder-gray-500 h-12"
                />
              </div>

              <div data-testid="booking-services-section">
                <Label className="text-white mb-3 block">Services * (Select all that apply)</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {serviceOptions.map((service) => {
                    const isSelected = selectedServices.includes(service);
                    const serviceId = `service-${service.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

                    return (
                      <label
                        key={service}
                        htmlFor={serviceId}
                        className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-blue-600/20 border border-blue-500'
                            : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-600'
                        }`}
                        data-testid={`booking-service-option-${serviceId}`}
                      >
                        <input
                          id={serviceId}
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleServiceToggle(service)}
                          className="h-4 w-4 rounded border-zinc-500 bg-zinc-900 text-blue-600 focus:ring-blue-600 focus:ring-offset-zinc-900"
                          data-testid={`booking-service-checkbox-${serviceId}`}
                        />
                        <span className="text-white text-sm flex-1">
                          {service}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {selectedServices.length === 0 && (
                  <p className="text-gray-500 text-sm mt-2" data-testid="booking-service-selection-help">Please select at least one service</p>
                )}
              </div>

              <div>
                <Label className="text-white mb-2 block">Vehicle Type *</Label>
                <Select value={formData.vehicleType} onValueChange={(value) => setFormData({ ...formData, vehicleType: value })} required>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-12">
                    <SelectValue placeholder="Select vehicle type" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                    {vehicleTypes.map((type) => (
                      <SelectItem key={type} value={type} className="hover:bg-zinc-700">
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white mb-2 block">Vehicle Make</Label>
                  <Input
                    type="text"
                    name="vehicleMake"
                    placeholder="e.g., Toyota"
                    value={formData.vehicleMake}
                    onChange={handleChange}
                    className="bg-zinc-800 border-zinc-700 text-white placeholder-gray-500 h-12"
                  />
                </div>
                <div>
                  <Label className="text-white mb-2 block">Vehicle Model</Label>
                  <Input
                    type="text"
                    name="vehicleModel"
                    placeholder="e.g., Corolla"
                    value={formData.vehicleModel}
                    onChange={handleChange}
                    className="bg-zinc-800 border-zinc-700 text-white placeholder-gray-500 h-12"
                  />
                </div>
              </div>

              <div>
                <Label className="text-white mb-2 block">Additional Notes</Label>
                <Textarea
                  name="message"
                  placeholder="Any special requests or information we should know?"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="bg-zinc-800 border-zinc-700 text-white placeholder-gray-500 resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="booking-form-submit-button"
                className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold h-12 text-lg"
              >
                {isSubmitting ? 'Submitting...' : 'Request Booking'}
              </Button>
            </form>
          </div>

          {/* Info Section */}
          <div className="space-y-6">
            <div className="bg-zinc-900 rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-white mb-4">What to Expect</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start space-x-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>We&rsquo;ll confirm the next available date. €25 deposit payable to secure slot.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>Courtesy collection available within 15km or drop off at our workshop at your convenience.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>Professional service with premium products</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>Your vehicle returned looking amazing</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-white mb-4">Contact Us</h3>
              <p className="text-blue-100 mb-4">
                Have questions? Call us or send an email:
              </p>
              <div className="space-y-2">
                <a href="tel:0896536680" className="block text-white hover:text-amber-500 font-semibold transition-colors">
                  089 653 6680
                </a>
                <a href="mailto:kingdomadbooking@gmail.com" className="block text-white hover:text-amber-500 transition-colors break-all">
                  kingdomadbooking@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;